<script setup lang="ts">
import OrderReference from './OrderReference.vue';
import OrderFunding from './OrderFunding.vue';
import { ref, computed, onMounted, watch } from 'vue';
import {
  readOrderReceipts,
  activeTravelerName,
  selectTravelerName,
  normalizeTravelerName,
  cancellationLabel,
  modificationLabel,
  type Cancellation,
  type Modification,
  type OrderFunding as Funding,
} from '../order-management';
const props = defineProps<{
  revision: number;
  initiallyOpen?: boolean;
  navigate?: (url: string) => void;
}>();
type Order = {
  adminRefusal?: import('../order-management').AdminRefusal;
  deliveryRequestId: string;
  offer: string;
  apartmentName: string;
  travelerName: string;
  canCancel: boolean;
  canModify: boolean;
  amountMinor: number;
  selectedBoxOfferId: string;
  cancellation: Cancellation | null;
  modifications?: Modification[];
  checkoutUrl?: string;
  funding?: Funding;
};
type Offer = {
  offerId: string;
  displayName: string;
  amountMinor: number;
  priceVersion: string;
};
const traveler = ref(activeTravelerName());
watch(traveler, selectTravelerName);
const visibleOrders = computed(() =>
  orders.value.filter(
    (order) =>
      Boolean(traveler.value.trim()) &&
      normalizeTravelerName(order.travelerName ?? '') === normalizeTravelerName(traveler.value),
  ),
);
const orders = ref<Order[]>([]),
  offers = ref<Offer[]>([]);
const busy = ref(false),
  opened = ref(props.initiallyOpen ?? false),
  error = ref(''),
  confirming = ref(''),
  editing = ref(''),
  selected = ref(''),
  paying = ref('');
const money = (amount: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount / 100);
const last = (o: Order) => o.modifications?.at(-1);
const currentOffer = () => offers.value.find((o) => o.offerId === selected.value);
async function request(
  id: string,
  action: string,
  extra: Record<string, unknown> = {},
): Promise<Order> {
  const receipt = readOrderReceipts().find((r) => r.deliveryRequestId === id);
  if (!receipt) throw new Error('Cette commande n’est pas accessible depuis ce navigateur.');
  const response = await globalThis.fetch(`/api/orders/${encodeURIComponent(id)}/${action}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ credential: receipt.credential, ...extra }),
  });
  if (!response.ok)
    throw new Error(
      'Impossible de modifier cette commande. Actualisez : son prix ou son traitement a pu changer.',
    );
  return (await response.json()) as Order;
}
async function load(): Promise<void> {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  const result: Order[] = [];
  for (const receipt of readOrderReceipts()) {
    try {
      result.push(await request(receipt.deliveryRequestId, 'status'));
    } catch {
      error.value =
        'Certaines commandes ne sont pas accessibles dans ce navigateur. Contactez l’équipe.';
    }
  }
  orders.value = result.reverse();
  traveler.value = activeTravelerName();
  busy.value = false;
}
async function act(order: Order, action: string): Promise<void> {
  if (busy.value) return;
  const offer = currentOffer();
  if (action === 'modify' && !offer) return;
  busy.value = true;
  error.value = '';
  try {
    let updated = await request(
      order.deliveryRequestId,
      action,
      action === 'modify'
        ? {
            offerId: offer!.offerId,
            amountMinor: offer!.amountMinor,
            priceVersion: offer!.priceVersion,
          }
        : action === 'cancel'
          ? {}
          : { modificationId: last(order)?.id },
    );
    if (action === 'modify' && last(updated)?.paymentRequired) {
      editing.value = '';
      orders.value = orders.value.map((o) =>
        o.deliveryRequestId === updated.deliveryRequestId ? updated : o,
      );
      updated = await request(updated.deliveryRequestId, 'pay-modification', {
        modificationId: last(updated)!.id,
      });
    }
    if (['pay-modification', 'modify'].includes(action) && updated.checkoutUrl) {
      const url = new globalThis.URL(updated.checkoutUrl);
      if (url.protocol !== 'https:' || url.username || url.password)
        throw new Error('Destination de paiement invalide.');
      (props.navigate ?? ((value: string) => globalThis.location.assign(value)))(
        url.toString(),
      );
      return;
    }
    orders.value = orders.value.map((o) =>
      o.deliveryRequestId === updated.deliveryRequestId ? updated : o,
    );
    confirming.value = '';
    editing.value = '';
    paying.value = '';
  } catch (cause) {
    error.value = (cause as Error).message;
  } finally {
    busy.value = false;
  }
}
async function edit(order: Order): Promise<void> {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  offers.value = [];
  selected.value = '';
  try {
    const response = await globalThis.fetch('/api/box-offers');
    if (!response.ok) throw new Error('Catalogue indisponible. Réessayez.');
    const body = await response.json();
    offers.value = body.boxOffers.filter((o: Offer) => o.offerId !== order.selectedBoxOfferId);
    editing.value = order.deliveryRequestId;
  } catch (cause) {
    error.value = (cause as Error).message;
  } finally {
    busy.value = false;
  }
}
onMounted(load);
watch(() => props.revision, load);
</script>
<template>
  <section v-if="orders.length || error || props.initiallyOpen" class="order-actions">
    <button
      class="danger entry"
      type="button"
      :aria-expanded="opened"
      @click="opened = !opened"
    >
      Annuler / modifier une commande
    </button>
    <div v-if="opened">
      <p v-if="error" role="alert">{{ error }}</p>
      <p v-if="!busy && !orders.length && !error">
        Aucune commande accessible dans ce navigateur. Utilisez le navigateur de votre commande
        ou contactez l’équipe.
      </p>
      <label
        >Nom utilisé pour la commande<input
          v-model="traveler"
          autocomplete="name"
          maxlength="128"
      /></label>
      <p v-if="!traveler.trim()">
        Saisissez votre nom pour retrouver vos commandes sur ce navigateur.
      </p>
      <p v-else-if="!visibleOrders.length">
        Aucune commande accessible pour ce nom sur ce navigateur.
      </p>
      <button type="button" :disabled="busy" @click="load">Actualiser les demandes</button>
      <article v-for="order in visibleOrders" :key="order.deliveryRequestId">
        <OrderReference :order-id="order.deliveryRequestId" />
        <h2>{{ order.offer }}</h2>
        <p>{{ order.apartmentName }}</p>
        <OrderFunding
          v-if="
            order.funding && (!order.adminRefusal || order.adminRefusal.status === 'REFUSED')
          "
          :funding="order.funding"
        />
        <p v-if="order.adminRefusal" role="status">
          {{
            order.adminRefusal.status === 'REFUSED'
              ? 'Commande refusée · empreinte libérée, aucun prélèvement.'
              : 'Commande refusée · libération bancaire à vérifier.'
          }}
          {{ order.adminRefusal.reason }}
        </p>
        <p v-if="order.cancellation" role="status">
          {{ cancellationLabel(order.cancellation) }}
        </p>
        <template v-if="last(order)">
          <p role="status">{{ modificationLabel(last(order)!) }}</p>
          <p>{{ last(order)!.from.displayName }} → {{ last(order)!.to.displayName }}</p>
          <p v-if="last(order)!.reason">{{ last(order)!.reason }}</p>
          <template v-if="!['APPLIED', 'REJECTED'].includes(last(order)!.status)">
            <p v-if="(last(order)!.deltaMinor ?? 0) > 0">
              Complément : {{ money(last(order)!.deltaMinor!) }}. La modification sera confirmée
              {{
                last(order)!.paymentBeforeControl
                  ? 'après règlement puis contrôle de faisabilité.'
                  : 'après contrôle et paiement.'
              }}
            </p>
            <p v-else-if="(last(order)!.deltaMinor ?? 0) < 0">
              Réduction demandée : {{ money(-last(order)!.deltaMinor!) }}. Validation manuelle
              administrateur après contrôle préparateur. La différence non prélevée sera libérée
              ; seule la somme déjà prélevée en trop sera remboursée.
            </p>
            <template
              v-if="
                last(order)!.paymentBeforeControl
                  ? last(order)!.paymentRequired
                  : ['CHECKED', 'PAYMENT_PENDING'].includes(last(order)!.status) &&
                    (last(order)!.deltaMinor ?? 0) > 0
              "
            >
              <button
                v-if="paying !== order.deliveryRequestId"
                type="button"
                :disabled="busy"
                @click="paying = order.deliveryRequestId"
              >
                Payer le complément
              </button>
              <div v-else class="payment" role="region" aria-label="Paiement complémentaire">
                <h3>Paiement complémentaire</h3>
                <p>Montant à payer : {{ money(last(order)!.deltaMinor!) }}</p>
                <button type="button" :disabled="busy" @click="act(order, 'pay-modification')">
                  Continuer vers le paiement
                </button>
                <button type="button" :disabled="busy" @click="paying = ''">Retour</button>
              </div>
            </template>
            <button
              v-if="
                !last(order)!.bankOperationPending &&
                !last(order)!.withdrawalRequested &&
                (['REQUESTED', 'CHECKED', 'CONTROL_FAILED'].includes(last(order)!.status) ||
                  (last(order)!.paymentBeforeControl &&
                    last(order)!.status === 'PAYMENT_PENDING'))
              "
              type="button"
              :disabled="busy"
              @click="act(order, 'withdraw-modification')"
            >
              Abandonner la modification
            </button>
          </template>
        </template>
        <button
          v-if="order.canModify && editing !== order.deliveryRequestId"
          class="danger"
          type="button"
          :disabled="busy"
          @click="edit(order)"
        >
          Modifier cette commande
        </button>
        <form v-if="editing === order.deliveryRequestId" @submit.prevent="act(order, 'modify')">
          <label :for="`replacement-offer-${order.deliveryRequestId}`">Nouvelle offre</label>
          <select
            :id="`replacement-offer-${order.deliveryRequestId}`"
            v-model="selected"
            :disabled="busy"
            required
          >
            <option value="">Choisir une offre</option>
            <option v-for="offer in offers" :key="offer.offerId" :value="offer.offerId">
              {{ offer.displayName }} · {{ money(offer.amountMinor) }}
            </option>
          </select>
          <p v-if="!offers.length">Aucune autre offre disponible.</p>
          <template v-if="currentOffer()">
            <p>
              Prix actuel de cette commande : {{ money(order.amountMinor) }}. Nouveau prix :
              {{ money(currentOffer()!.amountMinor) }}.
            </p>
            <p v-if="currentOffer()!.amountMinor > order.amountMinor">
              Complément à payer :
              {{ money(currentOffer()!.amountMinor - order.amountMinor) }}
              {{
                order.funding
                  ? 'avant le contrôle préparateur. Vous accéderez directement au paiement.'
                  : 'après contrôle préparateur.'
              }}
            </p>
            <p v-else-if="currentOffer()!.amountMinor < order.amountMinor">
              Réduction de
              {{ money(order.amountMinor - currentOffer()!.amountMinor) }} après contrôle
              préparateur et validation manuelle administrateur.
            </p>
            <p v-else>Aucun complément à payer. Contrôle préparateur avant confirmation.</p>
          </template>
          <button class="confirm" type="submit" :disabled="busy || !selected">
            Confirmer la demande de modification
          </button>
          <button type="button" :disabled="busy" @click="editing = ''">Retour</button>
        </form>
        <template v-if="order.canCancel">
          <button
            v-if="confirming !== order.deliveryRequestId"
            class="danger"
            type="button"
            :disabled="busy"
            @click="confirming = order.deliveryRequestId"
          >
            Annuler cette commande
          </button>
          <div v-else>
            <p>
              La demande suspend la préparation et la livraison. Le remboursement nécessite un
              contrôle préparateur et une validation administrateur.
            </p>
            <button class="danger" type="button" :disabled="busy" @click="act(order, 'cancel')">
              Confirmer la demande d’annulation
            </button>
            <button type="button" :disabled="busy" @click="confirming = ''">
              Conserver ma commande
            </button>
          </div>
        </template>
        <p v-if="!order.canCancel && !order.canModify && !order.cancellation && !last(order)">
          Pour modifier ou annuler cette commande, contactez l’équipe.
        </p>
      </article>
      <button type="button" @click="opened = false">Fermer</button>
    </div>
  </section>
</template>
<style scoped>
.order-actions {
  margin: 1rem 0;
  padding: 1rem;
  background: white;
  color: #172033;
  border-radius: 1rem;
  overflow-wrap: anywhere;
}
article {
  border-top: 1px solid #cbd5e1;
  padding: 1rem 0;
  margin-top: 1rem;
}
button {
  min-height: 44px;
  padding: 0.8rem;
  margin: 0.3rem 0;
  border: 0;
  border-radius: 0.6rem;
  background: #20214f;
  color: #fff;
  width: 100%;
}
button.confirm {
  background: #15803d;
  color: #ffffff;
  border-color: #15803d;
}
button.danger {
  background: #b91c1c;
  color: #fff;
  font-weight: 700;
}
button:disabled {
  opacity: 0.5;
}
select {
  width: 100%;
  max-width: 100%;
  min-height: 44px;
  background: white;
  color: #172033;
  padding: 0.6rem;
  border: 1px solid #64748b;
  border-radius: 0.5rem;
}
.payment {
  border: 2px solid #20214f;
  padding: 1rem;
  border-radius: 0.7rem;
}
</style>
